import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, GraduationCap, MessageCircleQuestion } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { API, TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";

function makeSessionId() {
  let sid = localStorage.getItem("hisd_session");
  if (!sid) {
    sid = (crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("hisd_session", sid);
  }
  return sid;
}

export const ChatInterface = ({ compact = false }) => {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(makeSessionId());

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || streaming) return;
    setInput("");
    const stamp = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setMessages((m) => [
      ...m,
      { id: `u-${stamp}`, role: "user", content: question },
      { id: `a-${stamp}`, role: "assistant", content: "" },
    ]);
    setStreaming(true);

    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionId.current, message: question, language: lang }),
      });

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          try {
            const data = JSON.parse(payload);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: last.content + data.delta };
                return copy;
              });
            }
          } catch (_) {
            /* ignore keepalive */
          }
        }
      }
    } catch (e) {
      console.error("Chat stream failed:", e);
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = {
          ...last,
          content: "Sorry, something went wrong. Please try again.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const starters = [t("starter_1"), t("starter_2"), t("starter_3"), t("starter_4")];
  const isEmpty = messages.length === 0;

  return (
    <div
      data-testid="chat-interface"
      className="flex h-full flex-col overflow-hidden rounded-3xl border border-black/5 bg-card shadow-[0_20px_60px_-30px_rgba(43,45,66,0.35)]"
    >
      <div
        ref={scrollRef}
        className="chat-scroll flex-1 space-y-4 overflow-y-auto px-6 py-8"
        style={{ minHeight: compact ? 320 : 560, maxHeight: compact ? 420 : 680 }}
        data-testid="chat-transcript"
      >
        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <span className="grid h-24 w-24 place-items-center rounded-[1.75rem] bg-secondary/15 text-secondary">
              <GraduationCap className="h-12 w-12" strokeWidth={1.5} />
            </span>
            <h4 className="mt-6 font-heading text-3xl font-semibold text-foreground sm:text-4xl">{t("chat_empty_title")}</h4>
            <p className="mt-3 max-w-xl text-lg leading-relaxed text-muted-foreground">{t("chat_empty_text")}</p>
            <div className="mt-8 grid w-full max-w-2xl gap-3 sm:grid-cols-2">
              {starters.map((s, i) => (
                <button
                  key={s}
                  data-testid={`starter-chip-${i}`}
                  onClick={() => send(s)}
                  className="flex items-center gap-3 rounded-2xl border border-border bg-background px-5 py-4 text-left text-base font-medium text-foreground/90 hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  <MessageCircleQuestion className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.75} />
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-bubble-${m.role}`}
                  className={`max-w-[85%] whitespace-pre-wrap rounded-3xl px-5 py-3.5 text-base leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-lg bg-primary text-primary-foreground"
                      : "rounded-bl-lg bg-surfaceAlt text-foreground"
                  }`}
                >
                  {m.content || (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" />
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="flex items-center gap-3 border-t border-border/70 bg-background px-5 py-5"
      >
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("chat_placeholder")}
          className="flex-1 rounded-2xl border border-border bg-card px-5 py-4 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
        <Button
          data-testid="chat-send-btn"
          type="submit"
          disabled={streaming || !input.trim()}
          className="h-14 rounded-2xl bg-primary px-6 text-base font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          <Send className="h-5 w-5" strokeWidth={2} />
          <span className="ml-2 hidden sm:inline">{t("chat_send")}</span>
        </Button>
      </form>
    </div>
  );
};
